import {
  Component,
  ElementRef,
  forwardRef,
  Input,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true,
    },
  ],
})
export class FileUploadComponent implements ControlValueAccessor {
  @Input() fileType: string = '*';

  fileName = signal('');
  fileSize = signal(0);
  uploadProgress = signal(0);
  filePreview = signal('');
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement> | undefined;
  selectedFile: File | null = null;
  uploadSuccess = false;
  uploadError = false;
  disabled = false;

  // ---- CVA callbacks ----
  private onChange: (value: File | null) => void = () => {};
  private onTouched: () => void = () => {};

  // ---- Required by ControlValueAccessor ----
  writeValue(file: File | null | string): void {
    // Null → reset
    if (!file) {
      this.resetState();
      return;
    }

    // If value is a file path string → ignore & reset
    if (typeof file === 'string') {
      this.resetState();
      return;
    }

    // Only proceed when it's a real File blob
    if (file instanceof File) {
      this.selectedFile = file;
      this.fileName.set(file.name);
      this.fileSize.set(Math.round(file.size / 1024));
      this.uploadSuccess = true;
      this.uploadError = false;

      const reader = new FileReader();
      reader.onload = (e) => {
        this.filePreview.set(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  }

  registerOnChange(fn: (value: File | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ---- Your existing logic, now calls onChange / onTouched ----

  onFileChange(event: Event): void {
    if (this.disabled) return;

    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.onTouched();
    this.validateAndUpload(file);
  }

  onFileDrop(event: DragEvent): void {
    if (this.disabled) return;

    event.preventDefault();
    const file = event.dataTransfer?.files[0] ?? null;
    this.onTouched();
    this.validateAndUpload(file);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  validateAndUpload(file: File | null): void {
    if (file && this.isValidFile(file)) {
      this.uploadFile(file);
    } else {
      this.handleInvalidFile();
      // notify form that the value is invalid / cleared
      this.onChange(null);
    }
  }

  isValidFile(file: File): boolean {
    if (!this.fileType) return true;

    const acceptedTypes = this.fileType.split(',');
    return acceptedTypes.some((type) => {
      const t = type.trim();
      if (t === '*') return true;
      if (t.endsWith('/*')) {
        const baseType = t.split('/')[0];
        return file.type.startsWith(baseType);
      }
      return file.type === t;
    });
  }

  uploadFile(file: File): void {
    this.selectedFile = file;
    this.fileSize.set(Math.round(file.size / 1024));

    const reader = new FileReader();
    reader.onload = (e) => {
      this.filePreview.set(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    this.uploadSuccess = true;
    this.uploadError = false;
    this.fileName.set(file.name);

    // ✅ Propagate to reactive form
    this.onChange(file);
  }

  handleInvalidFile(): void {
    this.uploadSuccess = false;
    this.uploadError = true;
  }

  removeImage(): void {
    if (this.disabled) return;

    this.resetState();
    this.onTouched();
    this.onChange(null); // ✅ Notify reactive form that value is cleared
  }

  private resetState(): void {
    this.selectedFile = null;
    this.fileName.set('');
    this.fileSize.set(0);
    this.filePreview.set('');
    this.uploadSuccess = false;
    this.uploadError = false;
    this.uploadProgress.set(0);

    // Clear native input if exists
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }
}
